<template>
  <div class="modal-card select-image-modal">
    <header class="modal-card-head">
      <p class="modal-card-title">Select and crop an image</p>
      <button
        type="button"
        class="delete"
        @click="$emit('close')" />
    </header>
    <section class="modal-card-body">
      <div v-if="!imageSrc" class="upload-area" @click="openFileInput" @dragover.prevent @drop.prevent="onDrop">
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="is-hidden"
          @change="onFileSelected" />
        <b-icon icon="image-plus" size="is-large" custom-class="has-text-grey-light" />
        <p class="upload-text">Drop an image here or click to upload</p>
      </div>
      <div v-else class="cropper-area">
        <Cropper
          ref="cropper"
          class="cropper"
          :src="imageSrc"
          :stencil-props="{ aspectRatio: 1 }"
          :resize-image="{ wheel: { ratio: 0.1 }, touch: true }"
          :move-image="true"
          image-restriction="none"
          :default-size="defaultCropSize"
        />
        <div class="toolbar">
          <b-button size="is-medium" icon-left="magnify-plus-outline" @click="zoomIn" />
          <b-button size="is-medium" icon-left="magnify-minus-outline" @click="zoomOut" />
          <span class="toolbar-divider" />
          <b-button size="is-medium" icon-left="rotate-left" @click="rotateLeft" />
          <b-button size="is-medium" icon-left="rotate-right" @click="rotateRight" />
          <span class="toolbar-divider" />
          <b-button size="is-medium" icon-left="flip-horizontal" @click="flipH" />
          <b-button size="is-medium" icon-left="flip-vertical" @click="flipV" />
        </div>
      </div>
    </section>
    <footer class="modal-card-foot">
      <b-button
        label="Remove"
        :disabled="!imageSrc"
        @click="resetImage" />
      <b-button
        label="Save"
        type="is-primary"
        :disabled="!imageSrc"
        @click="useImage" />
    </footer>
  </div>
</template>

<script>
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

export default {
  name: 'SelectImageModal',
  components: {
    Cropper
  },
  data() {
    return {
      imageSrc: null,
      flippedH: false,
      flippedV: false
    }
  },
  methods: {
    defaultCropSize({ imageSize }) {
      return {
        width: Math.min(imageSize.width, imageSize.height) * 0.9,
        height: Math.min(imageSize.width, imageSize.height) * 0.9
      }
    },
    openFileInput() {
      this.$refs.fileInput.click()
    },
    onFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.loadFile(file)
      }
    },
    onDrop(event) {
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.loadFile(file)
      }
    },
    loadFile(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imageSrc = e.target.result
      }
      reader.readAsDataURL(file)
    },
    resetImage() {
      this.imageSrc = null
      this.flippedH = false
      this.flippedV = false
    },
    zoomIn() {
      this.$refs.cropper.zoom(1.2)
    },
    zoomOut() {
      this.$refs.cropper.zoom(0.8)
    },
    rotateLeft() {
      this.$refs.cropper.rotate(-90)
    },
    rotateRight() {
      this.$refs.cropper.rotate(90)
    },
    flipH() {
      this.flippedH = !this.flippedH
      this.$refs.cropper.flip(true, false)
    },
    flipV() {
      this.flippedV = !this.flippedV
      this.$refs.cropper.flip(false, true)
    },
    useImage() {
      const { canvas } = this.$refs.cropper.getResult()
      if (canvas) {
        canvas.toBlob(
          blob => {
            this.$emit('image-selected', blob)
            this.$emit('close')
          },
          'image/jpeg',
          0.9
        )
      }
    }
  }
}
</script>

<style scoped>
.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  border: 2px dashed var(--border-medium, #cbd5e1);
  border-radius: var(--radius-sm, 8px);
  background: var(--bg-tertiary, #f1f3f9);
  cursor: pointer;
  transition: border-color var(--transition-fast, 0.15s ease),
              background var(--transition-fast, 0.15s ease);
}
.upload-area:hover {
  border-color: var(--feeds-green, #00857a);
  background: var(--feeds-green-light, #e6f5f3);
}

.upload-text {
  margin-top: 0.75rem;
  color: var(--text-secondary, #64748b);
  font-size: 0.95rem;
}

.cropper-area {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cropper {
  min-height: 400px;
  max-height: 72vh;
  border-radius: var(--radius-sm, 8px);
  overflow: hidden;
  background: #1a1a1a;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.toolbar-divider {
  width: 1px;
  height: 20px;
  background: var(--border-light, #e2e8f0);
  margin: 0 0.25rem;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .select-image-modal {
    width: 100vw;
    max-width: 100vw;
    margin: 0;
    border-radius: 0;
    height: 100vh;
    max-height: 100vh;
  }
  .select-image-modal .modal-card-body {
    flex: 1;
    overflow-y: auto;
  }
  .cropper {
    min-height: 250px;
    max-height: calc(100vh - 180px);
  }
  .upload-area {
    min-height: calc(100vh - 180px);
  }
}
</style>

<style>
/* Unscoped — targets the Buefy modal wrapper that sits outside this component */
.select-image-modal-wrapper .animation-content,
.select-image-modal-wrapper .animation-content .modal-card {
  width: 1000px !important;
  max-width: 95vw !important;
}
@media (max-width: 768px) {
  .select-image-modal-wrapper .animation-content,
  .select-image-modal-wrapper .animation-content .modal-card {
    width: 100vw !important;
    max-width: 100vw !important;
  }
}
</style>
